// src/pages/ProductDetailPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { usePlayerContext } from '../context/PlayerContext';
import { useAuthContext } from '../context/AuthContext';
import apiClient from '../api/apiClient';

import useSecureMedia from '../hooks/useSecureMedia';
import useHistoryTracking from '../hooks/useHistoryTracking';

import ProductHeader from '../components/Product/ProductHeader';
import RelatedProducts from '../components/Product/RelatedProducts';
import MediaFileList from '../components/Product/MediaFileList';
import VideoPlayer from '../components/players/VideoPlayer';
import PurchaseModal from '../components/Product/PurchaseModal';
import PlaceholderImage from '../assets/farzaneh.webp';

import '../pages/ProductDetailPage.css';

const ProductDetailPage = () => {
  const { categorySlug, subcategorySlug, slug } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, openAuthModal } = useAuthContext();

  const {
    currentMedia: activeAudio,
    isPlaying,
    audioRef,
    playMedia: startAudioPlayback,
    closePlayer: closeAudioPlayer,
  } = usePlayerContext();

  const { fetchSecuredUrl, currentMedia: currentPlaybackMedia, initialTime } = useSecureMedia();
  const mediaIdForHistory = currentPlaybackMedia?.id || activeAudio?.id;
  const currentTime = audioRef.current?.currentTime || 0;
  const { trackView } = useHistoryTracking(mediaIdForHistory, currentTime, isPlaying);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const abortControllerRef = useRef(null);

  // واکشی مجدد هنگام تغییر وضعیت لاگین
  useEffect(() => {
    if (isLoggedIn) {
      setRefetchTrigger(prev => prev + 1);
    }
  }, [isLoggedIn]);

  const fetchProduct = useCallback(async () => {
    if (!slug) {
      setError('مسیر محصول ناقص است.');
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let url;
    if (categorySlug) {
      url = `/products/${encodeURIComponent(categorySlug)}/`;
      if (subcategorySlug) url += `${encodeURIComponent(subcategorySlug)}/`;
      url += `${encodeURIComponent(slug)}/`;
    } else {
      url = `/products/${encodeURIComponent(slug)}/`;
    }

    console.log('📡 Fetching product:', url);
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(url, { signal: controller.signal });
      console.log('✅ Product fetched successfully:', response.data?.title);
      setProduct(response.data);
      setLoading(false);   // ✅ در صورت موفقیت اینجا false کن
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        console.log('🛑 Fetch cancelled');
        // لغو شده – loading را دست نزن
        return;
      }
      console.error('❌ Error fetching product:', err);
      setError(err.response?.status === 404 ? 'محصول یافت نشد.' : 'خطا در ارتباط با سرور.');
      setLoading(false);   // ✅ در صورت خطای واقعی اینجا false کن
    }
    // finally حذف شد – دیگر خطای err is not defined نداریم
  }, [categorySlug, subcategorySlug, slug]);

  useEffect(() => {
    fetchProduct();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchProduct, refetchTrigger]);

  const handleOpenPurchaseModal = useCallback(() => {
    if (!product) return;
    if (!isLoggedIn) {
      if (typeof openAuthModal === 'function') openAuthModal();
      return;
    }
    if (product.has_access) {
      alert('شما قبلاً به این محصول دسترسی دارید.');
      return;
    }
    setIsPurchaseModalOpen(true);
  }, [isLoggedIn, openAuthModal, product]);

  const handlePurchaseSuccess = useCallback(() => {
    setProduct(prev => {
      if (!prev) return prev;
      return { ...prev, has_access: true };
    });
    setRefetchTrigger(prev => prev + 1);
    setIsPurchaseModalOpen(false);
  }, []);

  const handleMediaPlay = async (mediaItem) => {
    if (!product || !mediaItem) return;
    const isFree = product.is_free || (product.price ?? 0) <= 0;
    const hasAccess = product.has_access;
    if (!isFree && !hasAccess) {
      alert('برای دسترسی به این محتوا باید محصول را خریداری کنید.');
      return;
    }
    let playbackUrl = mediaItem.media_url;
    let startTime = 0;
    if (!isFree && hasAccess && fetchSecuredUrl) {
      try {
        const result = await fetchSecuredUrl(mediaItem.id);
        if (result?.url) {
          playbackUrl = result.url;
          startTime = result.initialTime || 0;
        } else {
          throw new Error('دریافت لینک امن ناموفق');
        }
      } catch (err) {
        console.error('خطا در دریافت لینک امن:', err);
        alert('امکان پخش فایل وجود ندارد. لطفاً دوباره تلاش کنید.');
        return;
      }
    }
    if (!playbackUrl) {
      console.error('فایل رسانه URL ندارد:', mediaItem);
      alert('آدرس فایل در دسترس نیست. ممکن است دسترسی نداشته باشید.');
      return;
    }
    const mediaType = mediaItem.media_type;
    if (mediaType === 'VIDEO') {
      setActiveVideo({ ...mediaItem, url: playbackUrl, initialTime: startTime });
      if (closeAudioPlayer) closeAudioPlayer();
    } else if (mediaType === 'AUDIO') {
      const metadata = {
        title: mediaItem.title || 'بدون عنوان',
        artist: product.title || 'نام محصول',
        artworkUrl: mediaItem.thumbnail_url || product.cover_url || PlaceholderImage,
      };
      startAudioPlayback({
        ...mediaItem,
        url: playbackUrl,
        metadata,
        initialTime: startTime,
      });
      setActiveVideo(null);
    } else if (mediaType === 'PDF') {
      trackView(mediaItem.id);
      navigate('/pdf-viewer/', {
        state: { fileUrl: playbackUrl, title: mediaItem.title },
      });
      setActiveVideo(null);
      if (closeAudioPlayer) closeAudioPlayer();
    } else {
      alert(`فرمت فایل (${mediaType}) پشتیبانی نمی‌شود.`);
      setActiveVideo(null);
      if (closeAudioPlayer) closeAudioPlayer();
    }
  };

  const closeVideoPlayer = () => setActiveVideo(null);
  const isMediaPlaying = useCallback((mediaId) => activeAudio?.id === mediaId, [activeAudio]);

  // لاگ عیب‌یابی
  console.log('🔄 ProductDetailPage Render:', { loading, error, product: product?.title });

  if (loading) {
    console.log('⏳ Showing loading state');
    return <div className="loading-state text-center py-20">در حال بارگذاری ...</div>;
  }

  if (error || !product) {
    console.log('❌ Showing error state:', { error, product });
    return <div className="error-state text-center py-20 text-red-600">{error || 'محصول یافت نشد.'}</div>;
  }

  console.log('✅ Showing product:', product.title);

  // آماده‌سازی متا تگ‌ها بر اساس اطلاعات محصول
  const metaTitle = `${product.title} | فرزانه`;
  const plainDescription = product.long_description
    ? product.long_description.replace(/<[^>]*>/g, '').substring(0, 150) + '...'
    : 'محصول آموزشی در وب‌اپلیکیشن فرزانه';
  const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';
  const ogImage = product.cover_url || defaultOgImage;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={plainDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={plainDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={plainDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <div className="product-page-container min-h-screen pb-20">
        <ProductHeader
          product={product}
          navigateToBuy={handleOpenPurchaseModal}
          placeholderImage={PlaceholderImage}
          onViewDetailsClick={() => {}}
        />

        <PurchaseModal
          product={product}
          isOpen={isPurchaseModalOpen}
          onClose={() => setIsPurchaseModalOpen(false)}
          onSuccess={handlePurchaseSuccess}
        />

        <div className="main-content-area container mx-auto px-4 md:px-6">
          <MediaFileList
            mediaFiles={product.media_files || []}
            productHasAccess={product.has_access}
            onPlay={handleMediaPlay}
            onPause={() => {}}
            isMediaPlaying={isMediaPlaying}
          />

          {(categorySlug || slug) && (
            <RelatedProducts
              categorySlug={categorySlug}
              subcategorySlug={subcategorySlug}
              slug={slug}
            />
          )}
        </div>

        {activeVideo && (
          <VideoPlayer
            mediaItem={activeVideo}
            onClose={closeVideoPlayer}
            initialTime={activeVideo.initialTime}
          />
        )}
      </div>
    </>
  );
};

export default ProductDetailPage;